if (!customElements.get('movesmart-advice-hub')) {
  class MoveSmartAdviceHub extends HTMLElement {
    connectedCallback() {
      this.setupFilters();
      this.setupSearch();
      this.setupLoadMore();
    }

    setupFilters() {
      this.querySelectorAll('[data-advice-filter]').forEach((filter) => {
        filter.addEventListener('click', (event) => {
          event.preventDefault();

          const group = filter.dataset.filterGroup;
          const tag = filter.dataset.filterTag || '';
          let area = this.dataset.currentArea || '';
          let type = this.dataset.currentType || '';
          let condition = this.dataset.currentCondition || '';

          if (group === 'area') area = area === tag ? '' : tag;
          if (group === 'type') type = type === tag ? '' : tag;
          if (group === 'condition') condition = condition === tag ? '' : tag;

          const tags = [area, type, condition].filter(Boolean);
          const destination = tags.length
            ? `${this.dataset.blogUrl}/tagged/${tags.join('+')}`
            : this.dataset.blogUrl;

          window.location.assign(destination);
        });
      });
    }

    setupSearch() {
      const form = this.querySelector('[data-advice-search]');
      const input = form?.querySelector('[data-advice-search-input]');
      const suggestions = form?.querySelector('[data-advice-suggestions]');
      if (!form || !input || !suggestions || !this.dataset.predictiveUrl) return;

      let controller;
      let timer;

      const close = () => {
        suggestions.hidden = true;
        suggestions.replaceChildren();
        input.setAttribute('aria-expanded', 'false');
      };

      const open = () => {
        suggestions.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };

      const showMessage = (message) => {
        const paragraph = document.createElement('p');
        paragraph.className = 'ms-advice-search__message';
        paragraph.textContent = message;
        suggestions.replaceChildren(paragraph);
        open();
      };

      const render = (articles) => {
        const guideArticles = articles.filter((article) => article.url?.startsWith(this.dataset.blogPath));
        if (!guideArticles.length) {
          showMessage('No matching guides found. Try another word.');
          return;
        }

        const list = document.createElement('ul');
        guideArticles.forEach((article) => {
          const item = document.createElement('li');
          item.setAttribute('role', 'option');
          const link = document.createElement('a');
          link.href = article.url;
          link.textContent = article.title;
          link.tabIndex = -1;
          item.appendChild(link);
          list.appendChild(item);
        });
        suggestions.replaceChildren(list);
        open();
      };

      const search = async () => {
        const term = input.value.trim();
        if (term.length < 2) {
          close();
          return;
        }

        controller?.abort();
        controller = new AbortController();
        const url = new URL(this.dataset.predictiveUrl, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'article');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][fields]', 'title,body,tag');

        try {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Advice search failed');
          const payload = await response.json();
          render(payload?.resources?.results?.articles || []);
        } catch (error) {
          if (error.name !== 'AbortError') close();
        }
      };

      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(search, 250);
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        if (event.key === 'ArrowDown' && !suggestions.hidden) {
          const firstLink = suggestions.querySelector('a');
          if (firstLink) {
            event.preventDefault();
            firstLink.focus();
          }
        }
      });

      form.addEventListener('focusout', () => window.setTimeout(() => {
        if (!form.contains(document.activeElement)) close();
      }, 120));
    }

    setupLoadMore() {
      const wrap = this.querySelector('[data-advice-load-wrap]');
      const link = wrap?.querySelector('[data-advice-load-more]');
      const status = wrap?.querySelector('[data-advice-load-status]');
      const cards = this.querySelector('[data-advice-cards]');
      if (!wrap || !link || !cards || !this.dataset.nextUrl) return;

      let loading = false;

      const load = async () => {
        if (loading || !this.dataset.nextUrl) return;
        loading = true;
        link.setAttribute('aria-busy', 'true');
        link.textContent = 'Loading guides…';
        if (status) status.textContent = 'Loading more guides.';

        try {
          const response = await fetch(this.dataset.nextUrl, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Could not load more guides');
          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const incomingRoot = documentFragment.querySelector('[data-advice-root]');
          const incomingCards = incomingRoot?.querySelector('[data-advice-cards]');
          if (!incomingRoot || !incomingCards) throw new Error('Guide results were missing');

          const additions = Array.from(incomingCards.children);
          additions.forEach((card) => cards.appendChild(card));
          this.dataset.nextUrl = incomingRoot.dataset.nextUrl || '';

          if (!this.dataset.nextUrl) {
            wrap.remove();
          } else {
            link.href = this.dataset.nextUrl;
            link.textContent = 'Show more guides';
            link.removeAttribute('aria-busy');
            if (status) status.textContent = `${additions.length} more guides loaded.`;
          }
        } catch (error) {
          link.textContent = 'Try loading again';
          link.removeAttribute('aria-busy');
          if (status) status.textContent = 'The additional guides could not be loaded.';
        } finally {
          loading = false;
        }
      };

      link.addEventListener('click', (event) => {
        event.preventDefault();
        load();
      });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries[0]?.isIntersecting) load();
        }, { rootMargin: '300px 0px' });
        observer.observe(link);
      }
    }
  }

  customElements.define('movesmart-advice-hub', MoveSmartAdviceHub);
}

